import { pageRoutes } from 'constants/routing'
import { Dropdown } from 'react-bootstrap'
import 'lib/reactBootstrapReact19/dropdown'
import { useRouter } from 'next/router'
import NavDropdown from '../components/NavDropdown'
import styles from '../index.module.scss'
import LoginNavItemsWrapper from '../components/LoginNavItemsWrapper'
import { dashboardHeaderLinkText } from 'constants/dashboard'
import useSalesforceLead from 'hooks/salesforce/useSalesforceLead'
import { appendSalesforceLeadQuery } from 'hooks/salesforce/utils'
import { useUserDetailsQuery } from 'hooks/useUserDetailsQuery'
import { TreatmentFlags, treatmentFlagValues } from 'constants/treatments'
import useGetTreatment from 'hooks/treatments/useGetTreatment'
import useCoeDashboardTreatment from 'hooks/treatments/useCoeDashboardTreatment'

const SupportLoggedInNavItems: React.FC = () => {
  const isSalesForceB2BOn =
    useGetTreatment(TreatmentFlags.SALESFORCE_B2B_CASE_ACCOUNT) ===
    treatmentFlagValues.ON
  const { lead, hasLeadId, isEmbeddedInSf } = useSalesforceLead()
  const leadIdForHref = lead?.Id

  // Door into the new COE dashboard (Split: COE_DASHBOARD, the same rollout
  // switch the dashboard itself is gated on). On a user-details page the
  // userId is in the URL; resolve the user's primary patient and offer a jump
  // to the new dashboard for them. The query is disabled off user pages
  // (empty id) and deduped with the page's own fetch when on one, so it's free.
  const router = useRouter()
  const currentUserId = (router.query.userId as string) ?? ''
  const { canUseV2: isCoeDashboardEnabled } = useCoeDashboardTreatment()
  const [, patientsResult] = useUserDetailsQuery(currentUserId)
  // Prefer the patient who IS the user (relationship "Self") rather than
  // whichever patient happens to be first — a user with dependents can have
  // several, and [0] may not be the account holder.
  const patients = patientsResult?.data
  const primaryPatientId =
    patients?.find((p) => p.relationship?.toLowerCase() === 'self')?.id ??
    patients?.[0]?.id

  const renderTryV2DashboardItem = (resetToggle) => (
    <Dropdown.Item
      className={styles.dropdown_item}
      href={`${pageRoutes.COE_DASHBOARD}/patient/${primaryPatientId}?userId=${currentUserId}`}
      onClick={resetToggle}
    >
      Try new dashboard (V2)
    </Dropdown.Item>
  )

  const showTryV2Dashboard =
    isCoeDashboardEnabled && !!currentUserId && !!primaryPatientId

  const doesUserIdExist = lead?.User_UUID__c
  const salesForceUserDetailsPage = doesUserIdExist
    ? `${pageRoutes.DASHBOARD_SUPPORT_USER}?userId=${lead.User_UUID__c}`
    : pageRoutes.DASHBOARD_SUPPORT_USER_ACCOUNT_NOT_FOUND

  const renderUserDetailsDropdownItem = (resetToggle) => (
    <Dropdown.Item
      className={styles.dropdown_item}
      href={appendSalesforceLeadQuery(salesForceUserDetailsPage, leadIdForHref)}
      onClick={resetToggle}
    >
      User details
    </Dropdown.Item>
  )

  const renderDashboardDropdownItem = (resetToggle) => (
    <Dropdown.Item
      className={styles.dropdown_item}
      href={pageRoutes.DASHBOARD_SUPPORT}
      onClick={resetToggle}
    >
      {dashboardHeaderLinkText.DASHBOARD}
    </Dropdown.Item>
  )

  const renderInsuranceVerificationItem = (resetToggle) => (
    <Dropdown.Item
      className={styles.dropdown_item}
      href={appendSalesforceLeadQuery(
        pageRoutes.DASHBOARD_SUPPORT_USER_CHECK_INSURANCE_CARD_CAPTURE,
        leadIdForHref
      )}
      onClick={resetToggle}
    >
      {dashboardHeaderLinkText.INSURANCE_VERIFICATION}
    </Dropdown.Item>
  )

  const showUserDetailsInTheMenu = isSalesForceB2BOn
    ? isEmbeddedInSf && hasLeadId
    : hasLeadId

  const showDashboardInTheMenu = isSalesForceB2BOn
    ? !isEmbeddedInSf
    : !hasLeadId

  return (
    <div className="d-flex align-items-center">
      <NavDropdown navDropdownClass="py-2 pr-3">
        {({ resetToggle }) => (
          <LoginNavItemsWrapper
            loginNavItemHelpers={{
              resetToggle,
            }}
          >
            <div className="px-4 pt-3">
              {/* Salesforce Lead */}
              {showUserDetailsInTheMenu
                ? renderUserDetailsDropdownItem(resetToggle)
                : // Non - Salesforce
                showDashboardInTheMenu
                ? renderDashboardDropdownItem(resetToggle)
                : // Salesforce Case
                  null}
              {renderInsuranceVerificationItem(resetToggle)}
              {showTryV2Dashboard && renderTryV2DashboardItem(resetToggle)}
            </div>
          </LoginNavItemsWrapper>
        )}
      </NavDropdown>
    </div>
  )
}

export default SupportLoggedInNavItems
